/**
 * ProviderRecommendationRow Component
 *
 * Inline provider recommendations shown in collapsible row
 */

import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Button,
  Typography,
  Chip,
  Rating,
  Alert,
} from '@mui/material';
import { ProviderRecommendation, AlternativeAssignmentOptions } from '../types/intake.types';
import RecourseOptions from './RecourseOptions';

interface ProviderRecommendationRowProps {
  patientId: number;
  patientName: string;
  intakeDate: Date;
  recommendations: ProviderRecommendation[];
  alternatives?: AlternativeAssignmentOptions;
  targetUtilization: number;
  onAssign: (providerId: number, providerName: string) => void;
  onAssignAlternative: (date: Date, providerName?: string) => void;
  onAssignNextBest: (providerId: number, providerName: string) => void;
  onCreateProvisional: (date: Date) => void;
}

export default function ProviderRecommendationRow({
  patientId,
  patientName,
  intakeDate,
  recommendations,
  alternatives,
  targetUtilization,
  onAssign,
  onAssignAlternative,
  onAssignNextBest,
  onCreateProvisional,
}: ProviderRecommendationRowProps) {
  if (recommendations.length === 0) {
    // Show alternatives if available
    if (alternatives) {
      return (
        <Box sx={{ px: 2, py: 1 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            No providers available for <strong>{patientName}</strong> on{' '}
            {intakeDate.toLocaleDateString()} under target utilization.
          </Alert>
          <RecourseOptions
            patientId={patientId}
            alternatives={alternatives}
            onAssignAlternative={(pId, date, providerName) => onAssignAlternative(date, providerName)}
            onAssignNextBest={(pId, providerId, providerName) => onAssignNextBest(providerId, providerName)}
            onCreateProvisional={(pId, date) => onCreateProvisional(date)}
          />
        </Box>
      );
    }

    return (
      <Alert severity="warning" sx={{ my: 1 }}>
        No providers available for <strong>{patientName}</strong> on{' '}
        {intakeDate.toLocaleDateString()}. Try adjusting the target utilization or check other dates.
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom sx={{ px: 2 }}>
        Recommended Providers for {patientName} (Intake: {intakeDate.toLocaleDateString()}, Target
        Utilization: {targetUtilization}%)
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Provider</TableCell>
            <TableCell>Territory</TableCell>
            <TableCell align="right">Panel Size</TableCell>
            <TableCell align="right">Utilization</TableCell>
            <TableCell align="right">Available</TableCell>
            <TableCell align="right">Intake Capacity</TableCell>
            <TableCell align="center">Score</TableCell>
            <TableCell>Reason</TableCell>
            <TableCell align="center">Action</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {recommendations.map((rec) => {
            const intakeCapacity = Math.floor(rec.availableMinutes / 60);

            return (
              <TableRow
                key={rec.providerId}
                sx={{
                  backgroundColor: rec.isOverThreshold ? 'action.disabledBackground' : 'inherit',
                }}
              >
                <TableCell>
                  <strong>{rec.providerName}</strong>
                  <br />
                  <Typography variant="caption" color="text.secondary">
                    {rec.role}
                  </Typography>
                </TableCell>
                <TableCell>{rec.territory}</TableCell>
                <TableCell align="right">
                  <Chip
                    label={`${rec.panelSize} patients`}
                    size="small"
                    sx={{
                      bgcolor: 'grey.50',
                      color: 'grey.700',
                      border: '1px solid',
                      borderColor: 'grey.300',
                    }}
                  />
                </TableCell>
                <TableCell align="right">
                  <Chip
                    label={`${rec.utilizationPercent}%`}
                    size="small"
                    sx={{
                      bgcolor: rec.isOverThreshold
                        ? '#ffebee' // Light red
                        : rec.utilizationPercent > 70
                        ? '#fff3e0' // Light orange
                        : '#e8f5e9', // Light green
                      color: rec.isOverThreshold
                        ? '#c62828' // Dark red
                        : rec.utilizationPercent > 70
                        ? '#e65100' // Dark orange
                        : '#2e7d32', // Dark green
                      border: '1.5px solid',
                      borderColor: rec.isOverThreshold
                        ? '#ef5350' // Medium red
                        : rec.utilizationPercent > 70
                        ? '#ff9800' // Medium orange
                        : '#66bb6a', // Medium green
                      fontWeight: 600,
                    }}
                  />
                </TableCell>
                <TableCell align="right">
                  <Typography variant="body2">{rec.availableMinutes} min</Typography>
                </TableCell>
                <TableCell align="right">
                  <Chip
                    label={`${intakeCapacity} ${intakeCapacity === 1 ? 'visit' : 'visits'}`}
                    size="small"
                    sx={{
                      bgcolor: intakeCapacity >= 2
                        ? '#e8f5e9' // Light green
                        : intakeCapacity === 1
                        ? '#fff3e0' // Light orange
                        : '#ffebee', // Light red
                      color: intakeCapacity >= 2
                        ? '#2e7d32' // Dark green
                        : intakeCapacity === 1
                        ? '#e65100' // Dark orange
                        : '#c62828', // Dark red
                      border: '1.5px solid',
                      borderColor: intakeCapacity >= 2
                        ? '#66bb6a' // Medium green
                        : intakeCapacity === 1
                        ? '#ff9800' // Medium orange
                        : '#ef5350', // Medium red
                      fontWeight: 700,
                    }}
                  />
                </TableCell>
                <TableCell align="center">
                  <Rating value={rec.score / 20} max={5} precision={0.5} size="small" readOnly />
                  <br />
                  <Typography variant="caption" color="text.secondary">
                    {rec.score}/100
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="caption">{rec.reason}</Typography>
                </TableCell>
                <TableCell align="center">
                  {rec.isOverThreshold ? (
                    <Button variant="outlined" size="small" disabled>
                      Over Target
                    </Button>
                  ) : rec.availableMinutes < 60 ? (
                    <Button variant="outlined" size="small" disabled>
                      No Capacity
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => onAssign(rec.providerId, rec.providerName)}
                    >
                      Assign
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
}
